/**
 * ランディングセクションテンプレートの CRUD hooks。
 * Firestore: `landingSectionTemplates/{id}`
 *
 * 資料側 (documentSectionTemplates) と分離コレクション。
 * 資料は section.config 1 個だが、ランディングは content + style を保存する。
 */
import type { LandingSectionTemplate } from '@goenhub/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Timestamp,
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
} from 'firebase/firestore';

import { useAuth } from '@/hooks/useAuth';
import { db } from '@/lib/firebase';

export interface LandingSectionTemplateRecord extends LandingSectionTemplate {
  createdAtMs?: number;
}

const COLLECTION = 'landingSectionTemplates';

export function useLandingSectionTemplates() {
  return useQuery({
    queryKey: ['landingSectionTemplates'],
    queryFn: async (): Promise<LandingSectionTemplateRecord[]> => {
      const snap = await getDocs(
        query(collection(db, COLLECTION), orderBy('createdAt', 'desc')),
      );
      return snap.docs.map((d) => {
        const data = d.data() as LandingSectionTemplate;
        const ts = data.createdAt as Timestamp | undefined;
        return {
          ...data,
          id: d.id,
          createdAtMs: ts && typeof ts.toMillis === 'function' ? ts.toMillis() : undefined,
        };
      });
    },
  });
}

interface SaveArgs {
  name: string;
  description?: string;
  type: string;
  variant: string;
  content: Record<string, unknown>;
  style?: Record<string, unknown> | null;
}

export function useSaveLandingSectionTemplate() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (args: SaveArgs) => {
      if (!user?.uid) throw new Error('未ログインです');
      const payload = {
        name: args.name.trim(),
        description: args.description?.trim() || null,
        type: args.type,
        variant: args.variant,
        content: JSON.parse(JSON.stringify(args.content ?? {})),
        style: args.style ? JSON.parse(JSON.stringify(args.style)) : null,
        createdBy: user.uid,
        createdByName: user.displayName ?? user.email ?? null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      const ref = await addDoc(collection(db, COLLECTION), payload);
      return { id: ref.id };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['landingSectionTemplates'] });
    },
  });
}

export function useDeleteLandingSectionTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await deleteDoc(doc(db, COLLECTION, id));
      return { id };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['landingSectionTemplates'] });
    },
  });
}
